import { useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SideBar, useScreenResolution, ViewerContext } from "dash-ui";

import { useQuickTour } from "common/components/QuickTour";
import CollapsibleSideBar from "flow-components/Container/CollapsibleSideBar";
import { sidebarItemsList } from "flow-components/Container/Container.constants";
import { RoutesPath } from "flow-components/routes/routes";

function AppSideBar() {
  const { t } = useTranslation("common");
  const { account } = useContext(ViewerContext);
  const isSeeker = account?.isSeeker;
  const navigate = useNavigate();
  const location = useLocation();
  const {
    devices: { isMobile, isTablet },
  } = useScreenResolution();
  const isResponsive = isMobile || isTablet;
  const { activeStepKey } = useQuickTour();

  const pathsHiddenFromSeekers: string[] = [
    RoutesPath.Exchange,
    RoutesPath.Portfolios,
  ];

  const baseSidebarItemsList = isSeeker
    ? sidebarItemsList.filter(
        (item) => !pathsHiddenFromSeekers.includes(item.path)
      )
    : sidebarItemsList;

  const filteredSidebarItemsList = (() => {
    const searchItem: (typeof sidebarItemsList)[number] = {
      label: "search",
      icon: "search",
      path: RoutesPath.PropertiesSearch,
    };
    const propertiesIndex = baseSidebarItemsList.findIndex(
      (item) => item.path === RoutesPath.Properties
    );
    const withSearch =
      propertiesIndex === -1
        ? [searchItem, ...baseSidebarItemsList]
        : [
            ...baseSidebarItemsList.slice(0, propertiesIndex + 1),
            searchItem,
            ...baseSidebarItemsList.slice(propertiesIndex + 1),
          ];
    return isMobile
      ? withSearch.filter((item) => item.path !== RoutesPath.Exchange)
      : withSearch;
  })();

  const activeSidebarPath = filteredSidebarItemsList
    .map((item) => item.path)
    .filter(
      (p) => location.pathname === p || location.pathname.startsWith(`${p}/`)
    )
    .reduce((longest, p) => (p.length > longest.length ? p : longest), "");

  const sidebarItems = filteredSidebarItemsList.map(
    ({ label, icon, path, disabled }) => ({
      label: t(`labels.${label}`),
      icon,
      iconSize: icon === "search" ? 24 : undefined,
      isActive: path === activeSidebarPath,
      disabled,
      onClick: () => {
        navigate(path);
        document.title = t(`labels.browserTabTitles.${label}`);
      },
    })
  );

  return !isResponsive ? (
    <CollapsibleSideBar items={sidebarItems} />
  ) : (
    // The bottom nav hides itself while scrolling down, and the welcome tour's
    // map step scrolls down right before spotlighting it.
    <SideBar
      variation="secondary"
      items={sidebarItems}
      dataTour="bottom-nav"
      forceVisible={activeStepKey === "sidebar"}
    />
  );
}

export default AppSideBar;
